// packages/web/src/components/marketplace/BuyBox.tsx
// Amazon-style Buy Box — winner-first pattern with single CTA and trust bullets
import Check from '@mui/icons-material/Check';
import { Box, Typography } from '@mui/material';
import React, { useState } from 'react';

import AddToCartButton from './AddToCartButton';
import BuyBoxOption from './BuyBoxOption';
import { useBuyBoxLogic } from '../../hooks/useBuyBoxLogic';

interface ServiceData {
  // Required MarketplaceService fields
  id: string;
  name: string;
  description: string;
  category: string;
  brand_name: string;
  pricing_model: 'fixed' | 'varying' | 'variants' | 'multi_frequency';
  is_flagship: boolean;
  status: 'active' | 'inactive';
  requires_address_verification: boolean;

  // Optional MarketplaceService fields
  brand_description?: string;
  brand_logo_path?: string;
  default_pricing?: any;
  variants?: any;
  frequencies?: any[];
  geographic_restrictions?: any;
  metadata?: any;

  // Legacy/compatibility fields
  price_type?: string;
  product_type?: string;
  commission_rate?: number;
  service_type?: string;
  average_rating?: number;
  review_count?: number;
  owner_tenant_id?: string;
  owner_company?: string;
  company_description?: string;
  perks?: any[];
  winningPerk?: any;
  allPerks?: any[];
  one_time_charge?: number;
  nrc?: number;

  // Pricing data from API response (relational JOINs) - now nested under pricing object
  pricing?: {
    core_rate?: number;
    core_rate_min?: number;
    core_rate_max?: number;
    pricing_model?: string;
    billing_frequency?: string;
    varying_price_enabled?: boolean;
  };

  // Legacy fields for backward compatibility
  core_rate?: number;
  nrc_rate?: number;
  shipping_rate?: number;
  other_rate?: number;
  varying_price_enabled?: boolean;
}

interface BuyBoxProps {
  service: ServiceData;
  selectedVariants?: Record<string, string>;
  selectedVariantPrice?: number | null;
  selectedVariantKey?: string;
  selectedTerm?: number | null;
  serviceConfiguration?: Record<string, any>;
  ctaLabel?: string;
}

const TRUST_BULLETS_BASE = [
  'Managed installation included',
  '99.99% uptime SLA',
  '24/7 NOC support',
];
const PERK_BULLET = 'Perk delivery guaranteed by Aithon';

export const BuyBox: React.FC<BuyBoxProps> = ({
  service,
  selectedVariants = {},
  selectedVariantPrice,
  selectedVariantKey,
  selectedTerm,
  serviceConfiguration,
  ctaLabel,
}) => {
  const [showOtherOptions, setShowOtherOptions] = useState(false);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(0);

  const { sortedOptions, getPartnerDisplayName } = useBuyBoxLogic({ service });

  if (sortedOptions.length === 0) return null;

  const winnerOption = sortedOptions[selectedOptionIndex];

  const handleSelectOther = (absoluteIndex: number) => {
    setSelectedOptionIndex(absoluteIndex);
    setShowOtherOptions(false);
  };

  // Other options = all options except the currently selected winner
  const otherOptions = sortedOptions
    .map((opt, i) => ({ opt, i }))
    .filter(({ i }) => i !== selectedOptionIndex);

  // Does the winner have actual perks?
  const hasPerks = winnerOption?.perks?.length > 0 || winnerOption?.winningPerk || winnerOption?.allPerks?.length > 0 || winnerOption?.perk_type;
  // No-perk state: only a synthetic base record, no real competing partners
  const isNoPerkState = !hasPerks && (sortedOptions.length <= 1 || sortedOptions[0]?.id === 'base');
  const trustBullets = hasPerks ? [...TRUST_BULLETS_BASE, PERK_BULLET] : TRUST_BULLETS_BASE;

  // ── No-perk state: skip the competitive UI entirely, just show the CTA ──
  if (isNoPerkState) {
    return (
      <Box sx={{ mb: 3, bgcolor: 'white' }}>
        {/* CTA */}
        <Box
          sx={{
            mb: 0.5,
            '& .MuiButton-containedPrimary, & .MuiButton-contained': {
              backgroundColor: '#3b82f6',
              '&:hover': { backgroundColor: '#2563eb' },
            },
          }}
        >
          <AddToCartButton
            serviceId={service.id}
            label={ctaLabel || 'Get This Service'}
            selectedOption={winnerOption}
            variant="contained"
            size="large"
            fullWidth
            selectedVariants={selectedVariants}
            selectedVariantPrice={selectedVariantPrice}
            selectedVariantKey={selectedVariantKey}
            basePrice={(service as any).pricing?.core_rate || 0}
            monthlyRate={(service as any).pricing?.core_rate || 0}
            serviceType={service.product_type || service.service_type}
            varyingPriceEnabled={service.varying_price_enabled}
            serviceConfiguration={serviceConfiguration}
          />
        </Box>

        {/* Subtext */}
        <Typography
          variant="caption"
          sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', mt: 1, mb: 2 }}
        >
          No commitment until you confirm · Free to start
        </Typography>

        {/* Trust bullets */}
        <Box>
          {TRUST_BULLETS_BASE.map((bullet) => (
            <Box key={bullet} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Check sx={{ fontSize: '0.9rem', color: 'success.main', flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary">{bullet}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3, bgcolor: 'white' }}>
      {/* Section label — only shown when perks are competing */}
      <Typography
        variant="overline"
        sx={{
          color: 'text.secondary',
          fontWeight: 700,
          letterSpacing: 1,
          mb: 1.5,
          display: 'block',
          fontSize: '0.7rem',
        }}
      >
        INCLUDED WITH YOUR ORDER
      </Typography>

      {/* Winner card */}
      <BuyBoxOption
        option={winnerOption}
        isWinner={true}
        isFirst={selectedOptionIndex === 0}
        getPartnerDisplayName={getPartnerDisplayName}
      />

      {/* "Get This Service" CTA */}
      <Box
        sx={{
          mb: 0.5,
          '& .MuiButton-containedPrimary, & .MuiButton-contained': {
            backgroundColor: '#3b82f6',
            '&:hover': { backgroundColor: '#2563eb' },
          },
        }}
      >
        <AddToCartButton
          serviceId={service.id}
          label={ctaLabel || 'Get This Service'}
          selectedOption={winnerOption}
          variant="contained"
          size="large"
          fullWidth
          selectedVariants={selectedVariants}
          selectedVariantPrice={selectedVariantPrice}
          selectedVariantKey={selectedVariantKey}
          basePrice={(service as any).pricing?.core_rate || 0}
          monthlyRate={(service as any).pricing?.core_rate || 0}
          serviceType={service.product_type || service.service_type}
          varyingPriceEnabled={service.varying_price_enabled}
          serviceConfiguration={serviceConfiguration}
        />
      </Box>

      {/* Subtext */}
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          textAlign: 'center',
          color: 'text.secondary',
          mt: 1,
          mb: 2,
        }}
      >
        No commitment until you confirm · Free to start
      </Typography>

      {/* Trust bullets */}
      <Box>
        {trustBullets.map((bullet) => (
          <Box
            key={bullet}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}
          >
            <Check sx={{ fontSize: '0.9rem', color: 'success.main', flexShrink: 0 }} />
            <Typography variant="body2" color="text.secondary">
              {bullet}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Other options toggle and cards (below CTA + trust content) */}
      {sortedOptions.length > 1 && (
        <Box sx={{ mt: 2 }}>
          <Typography
            component="span"
            variant="body2"
            onClick={() => setShowOtherOptions((v) => !v)}
            sx={{
              color: '#3b82f6',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            {showOtherOptions
              ? '▲ Hide other options'
              : `▼ ${sortedOptions.length - 1} other partners offer this service`}
          </Typography>

          {showOtherOptions && (
            <Box sx={{ mt: 1 }}>
              {otherOptions.map(({ opt, i }) => (
                <BuyBoxOption
                  key={opt.id || i}
                  option={opt}
                  isWinner={false}
                  isFirst={i === 0}
                  getPartnerDisplayName={getPartnerDisplayName}
                  onSelect={() => handleSelectOther(i)}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default BuyBox;
